
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";

export function SearchDialog() {
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const { data: searchResults = [], refetch } = useQuery({
    queryKey: ["search", search],
    queryFn: async () => {
      if (!search) return [];
      
      console.log('Searching for:', search);
      
      const { data, error } = await supabase
        .from("profiles")
        .select("id, username, full_name, avatar_url")
        .or(`username.ilike.%${search}%, full_name.ilike.%${search}%`)
        .limit(5);

      if (error) {
        console.error("Search error:", error);
        return [];
      }

      console.log('Search results:', data);
      return data || [];
    },
    enabled: false,
  });

  const handleSearch = () => {
    if (search.length > 0) {
      refetch();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleProfileClick = (profile: { id: string, username?: string | null }) => {
    // Navigate using username if available, otherwise use ID
    const path = profile.username ? `/profile/${profile.username}` : `/profile/${profile.id}`;
    navigate(path);
  };

  return (
    <div className="relative w-full max-w-xl mx-auto">
      <div className="flex items-center border rounded-lg shadow-sm">
        <Search className="absolute left-3 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search profiles..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            if (e.target.value.length > 0) {
              handleSearch();
            }
          }}
          onKeyUp={handleKeyPress}
          className="pl-9 w-full focus-visible:ring-1"
        />
      </div>
      
      {search.length > 0 && (
        <div className="absolute w-full mt-1 rounded-md border bg-popover shadow-md">
          <Command>
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup heading="Profiles">
                {searchResults?.map((profile) => (
                  <CommandItem
                    key={profile.id}
                    onSelect={() => handleProfileClick(profile)}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={profile.avatar_url} />
                      <AvatarFallback>{profile.username?.[0]?.toUpperCase() || "U"}</AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">
                        {profile.username || "Anonymous"}
                      </span>
                      {profile.full_name && (
                        <span className="text-xs text-muted-foreground">
                          {profile.full_name}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  );
}
